'use client';

import { useTranslations } from 'next-intl';
import { ADDED_TO_BASKET_MODAL_ID, PRODUCT_INFO_QUICK_VIEW_MENU_ID } from '@headless-workspace/config';
import { unwrapResultSuccess } from '@headless-workspace/core-domain';
import { useToasts, useModalActions, useModalState } from '@headless-workspace/core-ui';
import { IconName } from '@headless-workspace/glow-ds';
import { ProductType, useBasketMutations, useEngraveContext, useProductDeliveryInfo } from '../../../../client';
import {
    AllStoresStockStatus,
    BasketProductValue,
    EngraveFormReturnType,
    WarehouseStockStatus,
} from '../../../../server';

type UseProductBasketCTAsParams = {
    productType: ProductType;
    productsToAdd: string[];
    productsSetAvailability?: boolean;
    hasShortLabel: boolean;
};

export enum ActionStatus {
    AddToBasketCondition = 'ADD_TO_BASKET_CONDITION',
    ChooseStoreCondition = 'CHOOSE_STORE_CONDITION',
    ChangeStoreCondition = 'CHANGE_STORE_CONDITION',
    NotifyMeCondition = 'NOTIFY_ME_CONDITION',
}

type CTA =
    | {
          actionStatus: ActionStatus.AddToBasketCondition;
          label: string;
          onClick: () => Promise<BasketProductValue | undefined>;
          variant: 'primary';
      }
    | {
          actionStatus: ActionStatus.ChooseStoreCondition | ActionStatus.ChangeStoreCondition;
          label: string;
          variant: 'primary';
          storeModalInfo: {
              title: string;
              closeButtonLabel: string;
          };
      }
    | {
          actionStatus: ActionStatus.NotifyMeCondition;
          iconName: IconName;
          label: string;
          variant: 'secondary';
      }
    | {
          actionStatus: null;
          label: string;
          variant: null;
      };

type UseProductBasketCTAsReturnType =
    | {
          isLoading: true;
      }
    | {
          isLoading: false;
          cta: CTA;
          isAddingToBasket: boolean;
      };

const useAddToBasket = (productType: ProductType, productsToAdd: string[]) => {
    const t = useTranslations('Discover.Pdp.information.basket');

    const { close: closeMenuModal } = useModalActions();
    const { isMenuModalOpen } = useModalState();
    const { notifySuccess, notifyError } = useToasts();
    const { engraveFormValues } = useEngraveContext();

    // Basket management functions
    const { isMutating: isAddingToBasket, add: addToBasket } = useBasketMutations();

    const productsToAddFormatted: { id: string; engrave?: EngraveFormReturnType }[] = productsToAdd.map((sku) => ({
        id: sku,
    }));

    // If the product is a set, we don't need to add engrave information to the product.
    if (productType !== ProductType.PRODUCT_SET && productsToAddFormatted[0]) {
        productsToAddFormatted[0].engrave = engraveFormValues;
    }

    const addToBasketHandler = async (): Promise<BasketProductValue | undefined> => {
        const addToBasketResult = await addToBasket(productsToAddFormatted);

        if (addToBasketResult.type !== 'success') {
            notifyError(t('toast.label.error'));
            return undefined;
        }

        // If the product is a set, we don't need to open the basket modal.
        if (productType === ProductType.PRODUCT_SET) {
            return undefined;
        }

        const customerBasket = unwrapResultSuccess(addToBasketResult);
        const product = customerBasket?.products.find((product) => product.productId === productsToAdd[0]);

        if (isMenuModalOpen(PRODUCT_INFO_QUICK_VIEW_MENU_ID) || isMenuModalOpen(ADDED_TO_BASKET_MODAL_ID)) {
            closeMenuModal(PRODUCT_INFO_QUICK_VIEW_MENU_ID, true);
            closeMenuModal(ADDED_TO_BASKET_MODAL_ID, true);
            notifySuccess(t('toast.label.success'));
            return undefined;
        }

        /**
         * If the basket modal is not open, we need to open it with the product added to the basket.
         * This applies in the case of adding to the basket on a PDP.
         */
        return product;
    };

    return {
        isAddingToBasket,
        addToBasketHandler,
    };
};

/**
 * This hook is used to manage the business rules for the product basket CTA.
 * It will check the product delivery information and return the appropriate CTA based on the product's availability.
 */
export const useProductBasketCTABusinessRules = ({
    productType,
    productsToAdd,
    productsSetAvailability,
    hasShortLabel,
}: UseProductBasketCTAsParams): UseProductBasketCTAsReturnType => {
    const discoverTranslations = useTranslations('Discover.Pdp.information.basket');
    const commonTranslations = useTranslations('Modal.stores');

    const { isAddingToBasket, addToBasketHandler } = useAddToBasket(productType, productsToAdd);

    // Retrieve product's delivery info
    const sku = productType !== ProductType.PRODUCT_SET && productsToAdd.length === 1 ? productsToAdd[0] : undefined;
    const { isLoading: isProductDeliveryInfoLoading, result: productDeliveryInfoResult } = useProductDeliveryInfo(sku);

    const addToBasketCTA: CTA = {
        label: hasShortLabel ? discoverTranslations('action.shortLabel.add') : discoverTranslations('action.add'),
        onClick: addToBasketHandler,
        variant: 'primary',
        actionStatus: ActionStatus.AddToBasketCondition,
    };

    const outOfStockCTA: CTA = {
        actionStatus: null,
        label: hasShortLabel
            ? discoverTranslations('action.shortLabel.outOfStock')
            : discoverTranslations('action.outOfStock'),
        variant: null,
    };

    if (productType === ProductType.PRODUCT_SET) {
        if (productsSetAvailability) {
            return {
                isLoading: false,
                cta: addToBasketCTA,
                isAddingToBasket,
            };
        }

        return {
            isLoading: false,
            cta: outOfStockCTA,
            isAddingToBasket: false,
        };
    }

    const productDeliveryInfo = unwrapResultSuccess(productDeliveryInfoResult);

    if (isProductDeliveryInfoLoading) {
        return { isLoading: true };
    }

    // When product delivery information is missing, we assume the product is out of stock.
    if (!productDeliveryInfo) {
        return {
            isLoading: isProductDeliveryInfoLoading,
            cta: outOfStockCTA,
            isAddingToBasket: false,
        };
    }

    const chooseStoreCTA: CTA = {
        label: hasShortLabel
            ? discoverTranslations('action.shortLabel.choose')
            : discoverTranslations('action.chooseStore'),
        variant: 'primary',
        actionStatus: ActionStatus.ChooseStoreCondition,
        storeModalInfo: {
            title: commonTranslations('label.storeAvailability'),
            closeButtonLabel: commonTranslations('action.chooseStore'),
        },
    };

    const changeStoreCTA: CTA = {
        label: hasShortLabel
            ? discoverTranslations('action.shortLabel.change')
            : discoverTranslations('action.changeStore'),
        variant: 'primary',
        actionStatus: ActionStatus.ChangeStoreCondition,
        storeModalInfo: {
            title: commonTranslations('label.storeAvailability'),
            closeButtonLabel: commonTranslations('action.changeStore'),
        },
    };

    const notifyMeCTA: CTA = {
        iconName: IconName.Notification,
        label: hasShortLabel
            ? discoverTranslations('action.shortLabel.outOfStockAndNotifyMe')
            : discoverTranslations('action.outOfStockAndNotifyMe'),
        variant: 'secondary',
        actionStatus: ActionStatus.NotifyMeCondition,
    };

    const getCTA = (): CTA => {
        if (productDeliveryInfo.isAvailable) {
            if (productDeliveryInfo.warehouse?.stockStatus === WarehouseStockStatus.Available) {
                return addToBasketCTA;
            }

            if (productDeliveryInfo.allStores?.stockStatus === AllStoresStockStatus.Available) {
                if (!productDeliveryInfo.currentStore) {
                    return chooseStoreCTA;
                }

                if (
                    productDeliveryInfo.currentStore.isAvailableInStore &&
                    productDeliveryInfo.currentStore.isClickAndCollectEnabled
                ) {
                    return addToBasketCTA;
                }

                return changeStoreCTA;
            }
        }

        return productDeliveryInfo.isNotifyMeEnabled ? notifyMeCTA : outOfStockCTA;
    };

    return { isLoading: isProductDeliveryInfoLoading, cta: getCTA(), isAddingToBasket };
};
